import _extends from "@babel/runtime/helpers/esm/extends";
import { ChatStreamError } from "../stream/ChatStreamError.js";

/**
 * Structural compatibility type for the Vercel AI SDK's UI Message Stream
 * (see https://ai-sdk.dev/docs/ai-sdk-ui/stream-protocol).
 *
 * Designed so users can pass `streamText(...).toUIMessageStream()` or
 * `response.body` from a `toUIMessageStreamResponse()` route without us
 * importing the `ai` package — the chunk shapes are mirrored locally.
 *
 * The AI SDK protocol is a near-superset of `ChatMessageChunk`; the only
 * shape exclusive to AI SDK that we handle specially is the `error` chunk.
 * Unknown `type` strings pass through untouched for forward-compat.
 */

/**
 * Structural mirror of `@ai-sdk/react`'s `useChat()` return value.
 * Only the fields the adapter consumes are typed; anything else on the real
 * object is preserved by structural assignability.
 */

function streamError(message) {
  return {
    code: 'STREAM_ERROR',
    message,
    source: 'stream',
    recoverable: true,
    retryable: true
  };
}
function getMessageText(message) {
  return message.parts.map(part => part.type === 'text' ? part.text : '').join('');
}
function parseStreamLine(rawLine) {
  // SSE framing prepends each event with `data: `. Strip it; ignore comments
  // (`:` prefix) and SSE field lines we don't care about (`event:`, `id:`, …).
  const trimmed = rawLine.trim();
  if (trimmed.length === 0 || trimmed.startsWith(':')) {
    return null;
  }
  if (!trimmed.startsWith('data:') && /^[\w-]+:/.test(trimmed)) {
    return null;
  }
  const payload = trimmed.startsWith('data:') ? trimmed.slice(5).trimStart() : trimmed;
  if (payload === '[DONE]') {
    return null;
  }
  return JSON.parse(payload);
}
function convertToChatStream(upstream, signal) {
  const reader = upstream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let syntheticIdCounter = 0;
  // Per-response synthetic ID used when the AI SDK doesn't supply one on
  // `start`/`finish`. `processStream` in the headless package requires a
  // `messageId` on the `start` chunk to bind subsequent text/reasoning
  // deltas to the right assistant message.
  let syntheticMessageId = null;
  const nextSyntheticMessageId = () => {
    syntheticIdCounter += 1;
    return `ai-sdk-msg-${syntheticIdCounter.toString(36)}`;
  };
  let hasAbortListener = false;
  const cancelReader = () => {
    if (hasAbortListener) {
      signal.removeEventListener('abort', cancelReader);
      hasAbortListener = false;
    }
    reader.cancel(signal.reason).catch(() => {});
  };
  const cleanupAbortListener = () => {
    if (hasAbortListener) {
      signal.removeEventListener('abort', cancelReader);
      hasAbortListener = false;
    }
  };
  if (signal.aborted) {
    cancelReader();
  } else {
    signal.addEventListener('abort', cancelReader, {
      once: true
    });
    hasAbortListener = true;
  }
  const emit = (chunk, controller) => {
    if (chunk.type === 'error') {
      const text = typeof chunk.errorText === 'string' ? chunk.errorText : 'AI SDK stream emitted an error chunk.';
      cleanupAbortListener();
      controller.error(new ChatStreamError(streamError(text)));
      return;
    }
    if (chunk.type === 'start' || chunk.type === 'finish' || chunk.type === 'abort') {
      const existing = chunk.messageId;
      if (typeof existing === 'string' && existing.length > 0) {
        syntheticMessageId = existing;
      } else {
        if (syntheticMessageId == null) {
          syntheticMessageId = nextSyntheticMessageId();
        }
        controller.enqueue(_extends({}, chunk, {
          messageId: syntheticMessageId
        }));
        return;
      }
    }
    controller.enqueue(chunk);
  };
  return new ReadableStream({
    async pull(controller) {
      // Drain enough source chunks to enqueue at least one downstream chunk
      // (or signal end). Multi-enqueue per pull is fully supported by all
      // WebStreams implementations we target, including jsdom's polyfill.
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const {
          value,
          done
        } = await reader.read();
        if (done) {
          const remaining = buffer + decoder.decode();
          buffer = '';
          if (remaining.trim().length > 0) {
            try {
              const parsed = parseStreamLine(remaining);
              if (parsed != null) {
                emit(parsed, controller);
              }
            } catch {
              // trailing garbage — ignore
            }
          }
          controller.close();
          cleanupAbortListener();
          return;
        }
        if (value === undefined) {
          continue;
        }
        if (isObjectChunk(value)) {
          emit(value, controller);
          return;
        }
        buffer += decoder.decode(coerceToBytes(value), {
          stream: true
        });
        let enqueued = false;
        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const rawLine = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          try {
            const parsed = parseStreamLine(rawLine);
            if (parsed != null) {
              emit(parsed, controller);
              enqueued = true;
            }
          } catch (err) {
            controller.error(new ChatStreamError(streamError(`AI SDK stream produced invalid JSON: ${err.message}`)));
            cleanupAbortListener();
            return;
          }
          newlineIndex = buffer.indexOf('\n');
        }
        if (enqueued) {
          return;
        }
      }
    },
    cancel(reason) {
      cleanupAbortListener();
      reader.cancel(reason).catch(() => {});
    }
  });
}

// Distinguishes an AI SDK object chunk from a raw byte chunk.
// We can't rely on `instanceof Uint8Array` because jsdom's
// `ReadableStream` polyfill loses typed-array identity in transit
// (the chunk arrives as a plain object with numeric-string keys).
// Every AI SDK UI Message Stream chunk has a string `type` discriminant,
// so that's a reliable signal.
function isObjectChunk(value) {
  return value !== null && typeof value === 'object' && typeof value.type === 'string';
}
function coerceToBytes(value) {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  // jsdom polyfill fallback: typed array transported as `{ '0': 123, '1': 34, …, byteLength?: n }`.
  const obj = value;
  const length = typeof obj.byteLength === 'number' ? obj.byteLength : Object.keys(obj).filter(k => /^\d+$/.test(k)).length;
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    out[i] = obj[i];
  }
  return out;
}
function createChatBasedAdapter(chat) {
  return {
    async sendMessage(input) {
      const userText = getMessageText(input.message);
      const files = input.attachments?.map(attachment => attachment.file);
      const lengthBefore = chat.messages.length;
      const onAbort = () => chat.stop();
      input.signal.addEventListener('abort', onAbort, {
        once: true
      });
      try {
        await chat.sendMessage(_extends({
          text: userText
        }, files && files.length > 0 ? {
          files
        } : {}));
      } finally {
        input.signal.removeEventListener('abort', onAbort);
      }
      const last = chat.messages[chat.messages.length - 1];
      const isNewAssistantReply = last != null && last.role === 'assistant' && chat.messages.length > lengthBefore;
      const replyText = isNewAssistantReply ? last.parts.map(part => part.type === 'text' ? part.text ?? '' : '').join('') : '';
      const replyId = isNewAssistantReply ? last.id : `reply-${input.message.id}`;
      const partId = `${replyId}-text`;
      return new ReadableStream({
        start(controller) {
          controller.enqueue({
            type: 'start',
            messageId: replyId
          });
          controller.enqueue({
            type: 'text-start',
            id: partId
          });
          controller.enqueue({
            type: 'text-delta',
            id: partId,
            delta: replyText
          });
          controller.enqueue({
            type: 'text-end',
            id: partId
          });
          controller.enqueue({
            type: 'finish',
            messageId: replyId,
            finishReason: 'stop'
          });
          controller.close();
        }
      });
    }
  };
}

/**
 * Adapter that bridges Vercel's `ai` SDK UI Message Stream into MUI X Chat.
 *
 * Two integration shapes:
 *
 * - `{ stream }`: hand us a `ReadableStream` produced by
 *   `streamText(...).toUIMessageStream()` or `(await fetch(...)).body`.
 *   Token-by-token streaming, AI SDK `error` chunks become `ChatStreamError`.
 * - `{ chat }`: hand us a `useChat()` return value. We drive sends through
 *   `chat.sendMessage`; the reply arrives whole once streaming completes.
 *
 * No runtime dependency on the `ai` package — the chunk type and chat shape
 * are mirrored structurally so callers retain full type info when `ai` is
 * installed and still type-check cleanly when it isn't.
 */
export function createAiSdkAdapter(options) {
  if ('chat' in options && options.chat) {
    return createChatBasedAdapter(options.chat);
  }
  const streamFn = options.stream;
  return {
    async sendMessage(input) {
      const upstream = await streamFn({
        message: input.message,
        messages: input.messages,
        attachments: input.attachments,
        metadata: input.metadata,
        signal: input.signal
      });
      return convertToChatStream(upstream, input.signal);
    }
  };
}